import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initPixel, trackEvent, isExcludedPath } from './metaPixel';
import { initGA, gaPageView } from './googleAnalytics';

/**
 * RouteAnalytics — San Tomas Tourism College
 * ---------------------------------------------------------------
 * Fires a pageview — to Meta and to GA4 — on every react-router
 * navigation.
 *
 * This is the single most important piece of the whole integration.
 * Without it, a visitor's entire session (home, programs, a course
 * page, the application form) is reported as one pageview on whichever
 * page they happened to land on. Ad optimisation, audience building and
 * every engagement metric are wrong.
 *
 * Renders nothing. Mount it once, inside <Router>.
 */
const RouteAnalytics = () => {
  const location = useLocation();

  /* Both trackers fire a pageview for the entry page when they start,
     so the first location this component sees must not be counted
     twice. A ref (not state) also survives React 18 StrictMode's
     double effect invocation in development. */
  const lastPath = useRef(null);

  useEffect(() => {
    initPixel();
    initGA();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;

    if (lastPath.current === null) {
      lastPath.current = path;          // entry page — already counted
      return;
    }
    if (lastPath.current === path) return;  // re-render, not a navigation

    lastPath.current = path;

    if (isExcludedPath(location.pathname)) return;

    trackEvent('PageView');
    gaPageView(path);
  }, [location.pathname, location.search]);

  return null;
};

export default RouteAnalytics;
